import { sleep } from "@/lib/utils"

import type { Api, IntegrationEvent, SmsChannelHealth, SmsLogEntry } from "../types"

// Офлайн-зеркало канала SMS. Показывает раздел «Интеграции» в том виде, в
// котором его увидит администратор на подключённом СМС-Центре: настроенный
// канал, отрицательный баланс (самая частая причина «коды перестали приходить»)
// и журнал со всеми исходами — доставлено, ждём статуса, отказ оператора.
const now = Date.now()
const minutesAgo = (m: number) => new Date(now - m * 60_000).toISOString()

const entry = (over: Partial<SmsLogEntry> & { id: string }): SmsLogEntry => ({
  provider: "smsc",
  providerMessageId: "0",
  phone: "+79990001122",
  purpose: "buyer_login",
  textPreview: "OSMIX code: ••••••",
  parts: 1,
  cost: 2.4,
  status: "delivered",
  providerStatus: 1,
  providerStatusName: "Доставлено",
  error: "",
  errorCode: null,
  sessionId: null,
  createdAt: minutesAgo(5),
  updatedAt: minutesAgo(4),
  ...over,
})

const log: SmsLogEntry[] = [
  entry({ id: "sms_1", providerMessageId: "101", createdAt: minutesAgo(3) }),
  entry({
    id: "sms_2",
    providerMessageId: "102",
    phone: "+79993334455",
    status: "sent",
    providerStatus: 0,
    providerStatusName: "Передано оператору",
    createdAt: minutesAgo(9),
  }),
  entry({
    id: "sms_3",
    providerMessageId: "103",
    phone: "+79995556677",
    purpose: "seller_login",
    status: "failed",
    providerStatus: 20,
    providerStatusName: "Невозможно доставить",
    cost: 2.4,
    createdAt: minutesAgo(42),
  }),
  entry({
    id: "sms_4",
    phone: "+79997778899",
    status: "failed",
    providerStatus: null,
    providerStatusName: "",
    error: "Недостаточно средств на счёте СМСЦ",
    errorCode: 3,
    cost: 0,
    createdAt: minutesAgo(70),
  }),
]

const health: SmsChannelHealth = {
  provider: "smsc",
  configured: true,
  missingConfig: [],
  sender: "OSMIX",
  balance: -4078.46,
  credit: 0,
  currency: "RUB",
  balanceError: "",
  balanceLow: true,
  stats: { windowHours: 24, sent: 4, delivered: 1, failed: 2, cost: 7.2 },
}

export const sms: Api["sms"] = {
  async health(windowHours = 24) {
    await sleep(140)
    return { ...health, stats: { ...health.stats, windowHours } }
  },
  async log(filter = {}) {
    await sleep(160)
    const phone = filter.phone?.trim() ?? ""
    const status = filter.status && filter.status !== "all" ? filter.status : ""
    const items = log.filter(
      (item) => (!phone || item.phone.includes(phone)) && (!status || item.status === status),
    )
    const offset = filter.offset ?? 0
    return { items: items.slice(offset, offset + (filter.limit ?? 50)), total: items.length }
  },
  async test(input) {
    await sleep(400)
    const dryRun = input.dryRun ?? true
    return {
      ok: true,
      dryRun,
      messageId: dryRun ? "" : "sms_mock",
      parts: 1,
      cost: 2.4,
      error: "",
      errorCode: null,
    }
  },
  async refreshStatuses() {
    await sleep(300)
    return { updated: 1 }
  },
  // Офлайн-журнал внешних обращений: удавшийся вебхук оплаты, неудавшаяся
  // регистрация отправления и возврат денег — три случая, ради которых раздел
  // и открывают.
  async events(filter = {}) {
    await sleep(200)
    const all: IntegrationEvent[] = [
      {
        id: "ige_demo_1",
        provider: "cloudpayments",
        direction: "in",
        event: "pay",
        status: "ok",
        refKind: "order",
        refId: "ord_demo_1",
        durationMs: 210,
        payload: { InvoiceId: "ord_demo_1", Amount: 4990, CardLastFour: "4242" },
        createdAt: new Date(Date.now() - 12 * 60_000).toISOString(),
      },
      {
        id: "ige_demo_2",
        provider: "sdek",
        direction: "out",
        event: "createOrder",
        status: "error",
        refKind: "order",
        refId: "ord_demo_2",
        payload: { shopId: "shop-handmade", attempts: 3, terminal: false },
        error: "shop has no ship-from origin",
        createdAt: new Date(Date.now() - 48 * 60_000).toISOString(),
      },
      {
        id: "ige_demo_3",
        provider: "cloudpayments",
        direction: "out",
        event: "refund",
        status: "ok",
        refKind: "order",
        refId: "ord_demo_3",
        payload: { returnId: "ort_demo_1", amount: 2490 },
        createdAt: new Date(Date.now() - 3 * 3_600_000).toISOString(),
      },
    ]
    return all.filter(
      (e) =>
        (!filter.provider || filter.provider === "all" || e.provider === filter.provider) &&
        (!filter.status || filter.status === "all" || e.status === filter.status) &&
        (!filter.refId || e.refId.includes(filter.refId)),
    )
  },
  async eventsSummary() {
    await sleep(150)
    return [
      { provider: "cloudpayments", total: 2, errors: 0, lastAt: new Date().toISOString() },
      { provider: "sdek", total: 1, errors: 1, lastAt: new Date().toISOString() },
    ]
  },
}
